import './index.css'
import { configure } from 'mobx'
import { setCacheKey } from 'reusable/utils/cache'
import variables from 'variables/variables'
import { reactRender } from 'reusable/react-render/react-render'
import RegionRouter from 'features/main/RegionRouter'
import { Analytics } from 'analytics/analytics'
import { Sentry } from 'sentry/sentry'
import { signIn, signOut, confirmSignIn } from '@aws-amplify/auth'
import { initThemeColors } from '@gap/shared/theme'
import { prefersDarkMode } from 'reusable/utils/utils'
import { canonicalStage } from '@gap/utils/stage'
import { captureAndStripOAuthCallbackSearch } from 'features/calendar/utils/oauth-callback-search'
import { singleRegionRedirectTarget } from 'features/main/singleRegionRedirect'
import { watchHoldingPage } from 'holding-page/watchHoldingPage'

// Read before the OAuth strip below so a redirected callback keeps its query.
const singleRegionTarget = singleRegionRedirectTarget({ isMultiRegion: variables.isMultiRegion, location: window.location })

// Before Sentry.init: session replay records location.href at start-up, independently of beforeSend.
captureAndStripOAuthCallbackSearch();

/** 
 * Export signIn and signOut from amplify
 * If the login request fails, delete the signIn function from the window object
 * This is to prevent the user from trying to login again and again
 */
(window as any).AmplifyAuth = {
   signIn: async ({ username, password }: { username: string, password: string }) => {
      try {
         return await signIn({ username, password })
      } catch (error: any) {
         delete (window as any).AmplifyAuth.signIn
         throw error
      }
   },
   confirmSignInAsEmail: async () => {
      await confirmSignIn({ challengeResponse: 'EMAIL' })
   },
   signOut
}

if (singleRegionTarget) {
   window.location.replace(singleRegionTarget)
} else {
   if (!variables.stage.includes('integration-testing')) {
      Analytics.init()
   }

   Sentry.initialiseIfEnabled()

   watchHoldingPage()

   // Set document title based on stage
   if (variables.stage.startsWith('dev-')) {
      document.title = `[local] ${variables.gapName}`
   } else if (variables.stage.match(/^(feature|bug|rc)-/)) {
      document.title = `[${variables.stage}] ${variables.gapName}`
   } else if (variables.stage === 'dev') {
      document.title = `[dev] ${variables.gapName}`
   } else if (variables.stage === 'test') {
      document.title = `[test] ${variables.gapName}`
   } else if (canonicalStage(variables.stage) === 'preprod') {
      document.title = `[preprod] ${variables.gapName}`
   }

   setCacheKey(variables.stage)

   // Apply dark primary palette before React renders (reads cached theme mode from localStorage)
   // Default to light mode - dark mode is dev-only feature
   const rawCachedThemeMode = localStorage.getItem('GapAppLocalThemeMode' + variables.stage)
   const cachedThemeMode = rawCachedThemeMode ? JSON.parse(rawCachedThemeMode) : undefined
   const isDark = cachedThemeMode === 'dark' || (cachedThemeMode === 'os-defined' && prefersDarkMode())
   initThemeColors(isDark)

   configure({
      enforceActions: 'never',
   })

   const container = document.getElementById('root')
   reactRender(() => <RegionRouter />, container)
}
